type WaiverData = {
  customerName: string;
  customerPhone: string;
  signatureDataUrl?: string;
  dateISO?: string;
  company?: {
    name?: string;
    tagline?: string;
    address?: string;
    phone?: string;
    email?: string;
    logoUrl?: string;
  };
};

function formatPhoneNumber(phone: string): string {
  if (!phone) return "";

  const digits = phone.replace(/\D/g, "").replace(/^1/, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

export function buildWaiverHtml({
  customerName,
  customerPhone,
  signatureDataUrl,
  dateISO = new Date().toISOString(),
  company = {
    name: "Presto Valet",
    tagline: "Certified Environmental Dry Cleaner",
    address: "37, O'Conner St, Bloomberg, UK",
    phone: "+360 245 675 467",
    email: "presto@prestovalet.net",
    logoUrl: new URL("/prestoform.png", window.location.origin).href,
  },
}: WaiverData): string {
  const fmt = (() => {
    const d = new Date(dateISO);
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = String(d.getFullYear());
    return `${mm}/${dd}/${yyyy}`;
  })();

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <title>Waiver of Address Requirement</title>
  <style>
    @page { size: A4 portrait; margin: 40mm; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
    body { font-family: -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial; color:#111; }
    .sheet { max-width: 420px; margin: 0 auto; }
    .header { text-align:center; margin-bottom:12px; }
    .logo { height:82px;width:auto; object-fit:contain; margin-bottom:6px; }
    .title { font-weight:700; font-size:18px; margin:2px 0; }
    .sub { font-size:12px; color:#333; margin-bottom:12px; }
    .muted { font-size:11px; color:#333;margin-bottom:35px; }
    .divider { border:0; border-top:1px dashed #888; margin:10px 0 35px; }
    .section-title { text-align:center; font-weight:700; font-size:12px; letter-spacing:.3px; text-transform:uppercase; margin:10px 0 25px; }
    .p { font-size:12px; line-height:1.35; text-align:justify; margin:6px 0 20px; }
    .siggrid { margin-top:50px; font-size:12px; }
    .row { display:grid; grid-template-columns:70px 8px 1fr; gap:8px; align-items:end; margin:10px 0 15px; }
    .line { display:inline-block; min-width:100%; border-bottom:1px dashed #444; }
    .small { font-size:11px; color:#333; }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="header">
      ${
        company.logoUrl
          ? `<img class="logo" src="${company.logoUrl}" alt="${company.name || ""}">`
          : ""
      }
      <div class="title">${company.name || ""}</div>
      ${company.tagline ? `<div class="sub">${company.tagline}</div>` : ""}
      <div class="muted">
        ${company.address || ""}<br>
        T.P.: ${company.phone || ""} | ${company.email || ""}
      </div>
    </div>

    <hr class="divider">

    <div class="section-title">WAIVER OF ADDRESS REQUIREMENT & ABANDONED GARMENTS NOTICE</div>

    <p class="p">
      I, the undersigned customer, acknowledge that under Virginia law, dry cleaners are required to notify customers
      by registered mail to their last known address to collect garments or other items left beyond the holding period.
      ${company.name || "The cleaner"} requests that customers provide a valid mailing address at the time of registration for this purpose.
    </p>

    <p class="p">
      I hereby choose not to provide my mailing address and understand that, as a result,
      ${company.name || "the cleaner"} will not be responsible for sending me notice of uncollected items.
      I further agree that any garments or items left for more than ninety (90) days after due date shall be considered abandoned.
    </p>

    <p class="p">
      By signing below, I release ${company.name || "the cleaner"}, its owners, officers, employees, and agents from any liability
      for loss, disposal, donation, or other disposition of items left beyond ninety (90) days. I understand and accept this policy in full.
    </p>

    <div class="siggrid">
      <div class="row">
        <div>Name</div><div>:</div>
        <div><div class="small">${customerName}</div></div>
      </div>
       <div class="row">
        <div>Contact No</div><div>:</div>
        <div><div class="small">${formatPhoneNumber(customerPhone)}</div></div>
      </div>
      <div class="row">
        <div>Signature</div><div>:</div>
        <div>${signatureDataUrl ? `<img src="${signatureDataUrl}" style="max-height:44px;max-width:100%;display:block;">` : `<span class="line"></span>`}</div>
      </div>
      <div class="row">
        <div>Date</div><div>:</div>
        <div></span><div class="small">${fmt}</div></div>
      </div>
    </div>
  </div>
</body>
</html>`;
}

export function openWaiverPrintWindow(data: WaiverData) {
  const printHtml = buildWaiverHtml(data).replace(
    "</body>",
    `<script>
    window.addEventListener('load', function () {
      setTimeout(function(){ window.print(); }, 100);
    });
    window.addEventListener('afterprint', function () { window.close(); });
  </script>
</body>`
  );

  const w = window.open("", "_blank", "width=1020,height=750");
  if (!w) return;
  w.document.open();
  w.document.write(printHtml);
  w.document.close();
}
